/* eslint-disable jsx-a11y/no-static-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */
import type { ReactNode } from 'react';
import React, { useMemo, useState } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { noop } from 'lodash';
import { InPortal, OutPortal, createHtmlPortalNode } from 'react-reverse-portal';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { useAppContext } from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { Backdrop } from 'Components/frameworks/backdrop/Backdrop';
import { Button } from 'Components/frameworks/button/Button';
import Portal from 'Components/frameworks/portal/Portal';
import { BottomNavbar } from 'Components/widgets/bottom-navbar/BottomNavbar';
type Props = {
  addedServicesNumber?: number;
  backLabel?: string;
  buyNowPayLaterPricing?: ReactNode;
  children?: any;
  closeLabel?: string;
  closeLayer?: () => void;
  closeLayerLabel?: string;
  continueAction?: () => void;
  continueBtn?: any;
  continueLabel?: string;
  forwardRef?: ReactRef;
  hasSelectedRate?: boolean;
  hideContinueButton?: boolean;
  isDisabled?: boolean;
  isFavorite?: boolean;
  isNextStep?: boolean;
  isPriceLoading: boolean;
  navbarRef?: ReactRef;
  nextPage?: string | null;
  onClickFavorite?: () => void;
  onCloseAllLayers?: () => void;
  openLabel?: string;
  overwriteContinueButton?: boolean;
  portalPosition: string;
  previousAction?: () => void;
  isOnLastPage?: boolean;
  previousPage?: string | null;
  price?: ProposalPriceDonation | CartPrice;
  shouldCloseAllLayers?: boolean;
  showCloseLayerBtn?: boolean;
  showPanel?: boolean;
  toggleReceiptPanel?: () => void;
  trackClickCloseReceipt?: () => void;
  trackClickOpenReceipt: () => void;
};
export const BottomNavbarLayer = ({
  addedServicesNumber,
  backLabel,
  buyNowPayLaterPricing,
  children,
  closeLabel,
  closeLayer,
  closeLayerLabel,
  continueAction,
  continueBtn,
  continueLabel = '',
  forwardRef,
  hasSelectedRate,
  hideContinueButton = false,
  isDisabled,
  isFavorite,
  isNextStep,
  isPriceLoading,
  navbarRef,
  nextPage,
  onClickFavorite,
  onCloseAllLayers,
  openLabel,
  overwriteContinueButton,
  portalPosition,
  previousAction,
  previousPage,
  isOnLastPage,
  price,
  shouldCloseAllLayers,
  showCloseLayerBtn,
  showPanel: showPanelProp,
  toggleReceiptPanel: toggleReceiptPanelProp,
  trackClickCloseReceipt = noop,
  trackClickOpenReceipt = noop,
}: Props) => {
  const portalNode = useMemo(() => createHtmlPortalNode(), []);
  const { isComingFromMobileApp } = useAppContext();
  const { isAppMounted } = useResponsive();
  const [showPanel, setShowPanel] = useState(false);
  const { trackClickEvent } = useTrackEvent();

  if (!isAppMounted) {
    return null;
  }

  const toggleReceiptPanel = () => {
    // toggle open
    if (!showPanel && !showPanelProp) {
      trackClickOpenReceipt();
    } else {
      trackClickCloseReceipt();
    }

    if (showPanelProp && toggleReceiptPanelProp) {
      toggleReceiptPanelProp();
    } else {
      setShowPanel(!showPanel);
    }
  };

  const closeAllLayer = () => {
    closeLayer?.();
    showPanel && setShowPanel(!showPanel);
  };

  if (shouldCloseAllLayers && showPanel) {
    closeAllLayer();
    onCloseAllLayers && onCloseAllLayers();
  }

  const closeLayerAndHidePanel = () => {
    setShowPanel(false);

    if (showPanelProp && toggleReceiptPanelProp) {
      toggleReceiptPanelProp();
    }
  };

  const isOpen = showPanel || showPanelProp;
  const showCloseLayerLink = showCloseLayerBtn || showPanel;
  return (
    <div className="isolate" data-testid="receipt">
      <InPortal node={portalNode}>
        <BottomNavbar
          addedServicesNumber={addedServicesNumber}
          backButton={{
            'aria-label': previousPage
              ? t('common.backTo', {
                  previousPage,
                })
              : t('common.common.previous'),
            'data-testid': 'BookingBottomNavbar-backNavigation',
            label: backLabel,
          }}
          className="z-2"
          closeButton={{
            label: closeLayerLabel,
          }}
          detailsButton={{
            'data-testid': 'receiptbutton',
            label: isOpen ? closeLabel : openLabel,
          }}
          forwardRef={forwardRef}
          hasCloseButton={Boolean(showCloseLayerLink && closeLayerLabel && !isComingFromMobileApp)}
          hasPrice={!!closeLayerLabel || (!showPanel && !showPanelProp && !!price)}
          hasSelectedRate={hasSelectedRate}
          isFavorite={isFavorite}
          isOnLastPage={isOnLastPage}
          isNextStep={isNextStep}
          isPriceLoading={isPriceLoading}
          navbarRef={navbarRef}
          onClickBack={previousAction}
          onClickClose={closeAllLayer}
          onClickDetails={toggleReceiptPanel}
          onClickFavorite={onClickFavorite}
          price={price}
          priceSlot={buyNowPayLaterPricing}
        >
          {!overwriteContinueButton && (
            <div
              className={classnames({
                'pointer-events-none hidden': hideContinueButton,
              })}
            >
              {continueBtn || (
                <Button
                  aria-label={
                    nextPage
                      ? t('common.continueTo', {
                          nextPage,
                        })
                      : null
                  }
                  className="w-full"
                  data-selector="BookingBottomNavbar-button-continue"
                  data-testid="withA11yButtonContinue"
                  isDisabled={isDisabled || isPriceLoading}
                  onClick={() => {
                    trackClickEvent(TRACK_EVENT_NAMES.CLICK_BOTTOM_BAR, {
                      detail_click: 'validate_step',
                    });
                    continueAction?.();
                  }}
                >
                  {continueLabel}
                </Button>
              )}
            </div>
          )}
        </BottomNavbar>
      </InPortal>
      <Portal name={portalPosition}>
        <OutPortal node={portalNode} />
      </Portal>
      {isOpen && (
        <Backdrop
          closeLabel={closeLabel}
          hasCloseButton
          onClose={closeLayerAndHidePanel}
          portalName={portalPosition}
        >
          <div
            className="relative z-1 flex h-full w-full flex-col"
            data-testid="BookingBottomNavbar-receiptContainer"
          >
            <div
              className="h-[10vh] cursor-pointer"
              data-testid="BookingBottomNavbar-receiptDummyClose"
              onClick={closeLayerAndHidePanel}
            />
            <div
              className="scrolling-touch flex flex-1 flex-col overflow-auto rounded-t-12 bg-white py-20"
              data-testid="price-ticket-layer"
            >
              <div className="grow overflow-x-hidden">{children}</div>
            </div>
          </div>
        </Backdrop>
      )}
    </div>
  );
};
